import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import { Provider, useSelector } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import PropTypes from "prop-types";
import ReduxToastr from "react-redux-toastr";
import { loadTranslations, setLocale } from "react-thunk-i18nify";

import translationsObject from "locales/root";

import { store, persistor } from "redux/store";

import USER_ROLES, { Admins } from "constants/userRoles";
import {
  AUTH,
  DASHBOARD,
  USER_SETTING,
  CONTACT,
  FAX_STATUS,
  NUMBER_PORTING_REQUEST,
  VOICEMAIL_BOX,
} from "constants/routes";

import "assets/css/main.scss";

import DebtorRoutes from "routes/Debtor";
import InvoiceRoutes from "routes/Invoice";
import AgreementRoutes from "routes/Agreement";
import PbxRoutes from "routes/Pbx";
import UserRoutes from "routes/User";
import UserLicenseRoutes from "routes/UserLicense";
import DidNumberRoutes from "routes/DidNumber";
import SettingRoutes from "routes/Setting";
import SubscriptionProductRoutes from "routes/SubscriptionProduct";
import CellPhoneRoutes from "routes/CellPhone";
import CreditNotesRoutes from "routes/CreditNote";
import VendorRoutes from "routes/Vendor";
import BillsRoutes from "routes/Bill";
import NumberPortingRequestRoutes from "routes/NumberPortingRequest";
import SipDeviceRoutes from "routes/SipDevice";
import TelecomRoutes from "routes/Telecom";
import QuotationRoutes from "routes/Quotation";

import Dashboard from "views/Dashboard/Dashboard";
import UserSettingIndex from "views/UserSetting/UserSettingIndex";
import ResetPassword from "views/Auth/ResetPassword";
import SetPassword from "views/Auth/SetPassword";
import LoginView from "views/Auth/Login";
import ContactIndex from "views/Contact/ContactIndex";
import FaxStatusDetail from "views/FaxStatus/FaxStatusDetail/FaxStatusDetail";
import VoicemailBoxDetail from "views/VoicemailBox/VoicemailBoxDetail";
import Forbidden from "views/Forbidden";
import ImportCustomerForm from "views/NumberPortingRequest/Import/ImportCustomerForm";
import VoicemailBoxEdit from "views/VoicemailBox/VoicemailBoxEdit";

import SideMenu from "components/Menu/SideMenu/SideMenu";
import UnauthorizedPage from "components/Auth/UnauthorizedPage";
import SessionExpiredModal from "components/Modal/SessionExpiredModal";

function PrivateRoute({ reduxStore, children, ...rest }) {
  const { isLoggedIn, currentUser } = useSelector(({ auth }) => auth);

  return (
    <Route
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
      render={({ location }) => {
        if (isLoggedIn) {
          if (currentUser.role === USER_ROLES.PBX_USER)
            return (
              <div className="app-content h-100">
                <UnauthorizedPage className="h-100" onlyMobileLogin />
              </div>
            );

          return children;
        }

        return (
          <Redirect
            to={{
              pathname: AUTH.LOGIN,
              state: {
                from:
                  location.pathname === AUTH.LOGIN
                    ? DASHBOARD.INDEX
                    : location.pathname,
              },
            }}
          />
        );
      }}
    />
  );
}

function AppRouter({ reduxStore }) {
  const { i18n } = reduxStore.getState();

  if (!i18n.locale) {
    store.dispatch(setLocale("da-DK"));
  }

  useEffect(() => {
    store.dispatch(loadTranslations(translationsObject));
  }, []);

  return (
    <div className="app">
      <ReduxToastr
        timeOut={4000}
        key={i18n.locale}
        newestOnTop={false}
        preventDuplicates
        position="top-right"
        getState={(state) => state.toastr}
        transitionIn="fadeIn"
        transitionOut="fadeOut"
        progressBar
        closeOnToastrClick
      />
      <Router>
        <Switch>
          <Route path={AUTH.LOGIN} component={LoginView} />
          <Route path={AUTH.FORGOT_PASSWORD} component={ResetPassword} />
          <Route path={AUTH.SET_PASSWORD} component={SetPassword} />
          <Route path={AUTH.FORBIDDEN} component={Forbidden} />
          <Route exact path={AUTH.INDEX}>
            <Redirect to={AUTH.LOGIN} />
          </Route>
          <Route
            exact
            path={NUMBER_PORTING_REQUEST.CUSTOMER_IMPORT_FORM}
            component={ImportCustomerForm}
          />

          <PrivateRoute reduxStore={reduxStore}>
            <SideMenu reduxStore={reduxStore} />
            <div className="app-content h-100">
              <Route
                exact
                path={DASHBOARD.INDEX}
                component={Admins(Dashboard)}
              />
              <Route
                exact
                path={USER_SETTING.INDEX}
                component={Admins(UserSettingIndex)}
              />
              <Route exact path={CONTACT.INDEX} component={ContactIndex} />
              <DebtorRoutes reduxStore={reduxStore} />
              <InvoiceRoutes />
              <CreditNotesRoutes />
              <BillsRoutes />
              <VendorRoutes />
              <AgreementRoutes />
              <PbxRoutes />
              <UserRoutes />
              <TelecomRoutes />
              <QuotationRoutes />

              <UserLicenseRoutes />
              <DidNumberRoutes />
              <Route path={FAX_STATUS.SHOW} component={FaxStatusDetail} />
              <SettingRoutes />
              <SubscriptionProductRoutes />
              <CellPhoneRoutes />
              <SipDeviceRoutes />
              <NumberPortingRequestRoutes />
              <Route
                exact
                path={VOICEMAIL_BOX.INDEX}
                component={VoicemailBoxDetail}
              />
              <Route path={VOICEMAIL_BOX.EDIT} component={VoicemailBoxEdit} />
            </div>
          </PrivateRoute>

          {/* Not working right now */}
          {/* <Route path="*" component={() => '404 page'} /> */}
        </Switch>
        <SessionExpiredModal />
      </Router>
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AppRouter reduxStore={store} />
      </PersistGate>
    </Provider>
  );
}

PrivateRoute.propTypes = {
  children: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  reduxStore: PropTypes.shape({
    getState: PropTypes.func,
  }).isRequired,
};

AppRouter.propTypes = {
  reduxStore: PropTypes.shape({
    getState: PropTypes.func,
  }).isRequired,
};

export default App;
